import { forwardRef, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { buildCloudinaryUrl } from "@/lib/cloudinaryUrl";
import { AUTO_BREAKPOINTS, type CoverFraming } from "@/lib/bannerFraming";

// Renders an artist banner image filling its parent. Used by the public
// ArtistPage and by the banner editors' previews, so the preview is exactly
// what visitors get. Keep in sync with dashboard/src/components/banner/.
//
//  - framing set:  whole image (never upscaled), placed with object-position
//                  and scaled around the focal point — pure CSS, so the same
//                  framing reads correctly at every banner width.
//  - framing null: automatic. Cloudinary crops around the detected subject
//                  at the strip's aspect ratio (per breakpoint on the page,
//                  or the single `aspect` a preview asks for).

const FULL_WIDTHS = [640, 1280, 1920, 2560, 3840];
const AUTO_WIDTHS = [640, 1280, 1920, 2880];

function srcSet(url: string, widths: number[], fillAspect?: number) {
  return widths
    .map((w) => `${buildCloudinaryUrl(url, { width: w, dpr: 1, fillAspect })} ${w}w`)
    .join(", ");
}

interface BannerImageProps {
  url: string;
  framing: CoverFraming | null;
  alt: string;
  /** Fixed strip aspect (previews). Omit on the page to follow breakpoints. */
  aspect?: number;
  /** Rendered width of the strip, for srcset selection. */
  sizes?: string;
  priority?: boolean;
  className?: string;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

export const BannerImage = forwardRef<HTMLImageElement, BannerImageProps>(function BannerImage(
  { url, framing, alt, aspect, sizes = "100vw", priority, className, onLoad },
  ref
) {
  const isCloudinary = buildCloudinaryUrl(url) !== url;
  const imgClass = cn("h-full w-full object-cover", className);
  const common = {
    ref,
    alt,
    onLoad,
    decoding: "async" as const,
    fetchPriority: priority ? ("high" as const) : undefined,
    draggable: false,
  };

  if (framing) {
    const origin = `${framing.x * 100}% ${framing.y * 100}%`;
    const style: CSSProperties = {
      objectPosition: origin,
      transformOrigin: origin,
      transform: framing.zoom !== 1 ? `scale(${framing.zoom})` : undefined,
    };
    return (
      <img
        {...common}
        src={isCloudinary ? buildCloudinaryUrl(url, { width: 1920, dpr: 1 }) : url}
        srcSet={isCloudinary ? srcSet(url, FULL_WIDTHS) : undefined}
        // Zoom enlarges the image, so ask for proportionally more pixels.
        sizes={framing.zoom !== 1 ? `calc(${framing.zoom} * ${sizes})` : sizes}
        className={imgClass}
        style={style}
      />
    );
  }

  if (!isCloudinary) {
    return <img {...common} src={url} className={imgClass} />;
  }

  if (aspect) {
    return (
      <img
        {...common}
        src={buildCloudinaryUrl(url, { width: 1280, dpr: 1, fillAspect: aspect })}
        srcSet={srcSet(url, AUTO_WIDTHS, aspect)}
        sizes={sizes}
        className={imgClass}
      />
    );
  }

  const fallback = AUTO_BREAKPOINTS[AUTO_BREAKPOINTS.length - 1].aspect;
  return (
    <picture>
      {AUTO_BREAKPOINTS.filter((b) => b.media).map((b) => (
        <source key={b.media} media={b.media} srcSet={srcSet(url, AUTO_WIDTHS, b.aspect)} sizes={sizes} />
      ))}
      <img
        {...common}
        src={buildCloudinaryUrl(url, { width: 1280, dpr: 1, fillAspect: fallback })}
        srcSet={srcSet(url, AUTO_WIDTHS, fallback)}
        sizes={sizes}
        className={imgClass}
      />
    </picture>
  );
});
